import { useEffect, useState } from 'react';
import {
  FaCheckCircle,
  FaExclamationTriangle,
  FaInfoCircle,
  FaTimes,
} from 'react-icons/fa';

const MessageToast = ({ type, message, onClose, duration = 5000 }) => {
  const [isVisible, setIsVisible] = useState(true);
  const [isAnimating, setIsAnimating] = useState(false);

  useEffect(() => {
    setIsAnimating(true);

    const timer = setTimeout(() => {
      handleClose();
    }, duration);

    return () => clearTimeout(timer);
  }, [duration]);

  const handleClose = () => {
    setIsAnimating(false);
    setTimeout(() => {
      setIsVisible(false);
      onClose();
    }, 300);
  };

  if (!isVisible) return null;

  const getToastStyles = () => {
    const baseStyles = `fixed top-6 left-1/2 transform -translate-x-1/2 z-50 max-w-lg w-full mx-4 rounded-xl shadow-2xl backdrop-blur-sm transition-all duration-300 ease-in-out`;

    switch (type) {
      case 'success':
        return `${baseStyles} bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 text-green-800`;
      case 'error':
        return `${baseStyles} bg-gradient-to-r from-red-50 to-rose-50 border border-red-200 text-red-800`;
      case 'info':
        return `${baseStyles} bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 text-blue-800`;
      default:
        return `${baseStyles} bg-white border border-gray-200 text-gray-800`;
    }
  };

  const getIcon = () => {
    switch (type) {
      case 'success':
        return <FaCheckCircle className="text-green-500 text-xl" />;
      case 'error':
        return <FaExclamationTriangle className="text-red-500 text-xl" />;
      case 'info':
        return <FaInfoCircle className="text-blue-500 text-xl" />;
      default:
        return <FaInfoCircle className="text-gray-500 text-xl" />;
    }
  };

  const animationClass = isAnimating
    ? 'animate-[slideInDown_0.3s_ease-out] opacity-100 translate-y-0'
    : 'opacity-0 -translate-y-4';

  return (
    <div className={`${getToastStyles()} ${animationClass}`}>
      <div className="flex items-start gap-3 p-4">
        <div className="flex-shrink-0 mt-0.5">{getIcon()}</div>
        <div className="flex-1">
          <p className="text-sm font-medium leading-relaxed">{message}</p>
        </div>
        <button
          onClick={handleClose}
          className="flex-shrink-0 ml-2 p-1 hover:bg-white/50 rounded-full transition-colors duration-200"
          aria-label="Închide mesajul"
        >
          <FaTimes className="text-sm opacity-60 hover:opacity-100" />
        </button>
      </div>

      {/* Progress bar */}
      <div className="h-1 bg-white/30 rounded-b-xl overflow-hidden">
        <div
          className="h-full bg-current opacity-30 rounded-b-xl transition-all duration-1000 ease-linear"
          style={{
            width: isAnimating ? '0%' : '100%',
            transitionDuration: `${duration}ms`,
          }}
        />
      </div>
    </div>
  );
};

// Hook pentru gestionarea mesajelor
export const useToastMessages = () => {
  const [messages, setMessages] = useState([]);

  const showMessage = (type, message, duration = 5000) => {
    const id = Date.now() + Math.random();
    setMessages((prev) => [...prev, { id, type, message, duration }]);
  };

  const removeMessage = (id) => {
    setMessages((prev) => prev.filter((msg) => msg.id !== id));
  };

  const showSuccess = (message, duration) =>
    showMessage('success', message, duration);
  const showError = (message, duration) =>
    showMessage('error', message, duration);
  const showInfo = (message, duration) =>
    showMessage('info', message, duration);

  const ToastContainer = () => (
    <>
      {messages.map((msg) => (
        <MessageToast
          key={msg.id}
          type={msg.type}
          message={msg.message}
          duration={msg.duration}
          onClose={() => removeMessage(msg.id)}
        />
      ))}
    </>
  );

  return {
    showSuccess,
    showError,
    showInfo,
    ToastContainer,
  };
};

export default MessageToast;
